import {
  DEFAULT_API_BASE,
  CipherNestApiClient,
  type TokenResponse,
} from "@ciphernest/api-client";
import {
  createOrganizationKeys,
  createProtectedAccountKeys,
  decryptBytes,
  decryptEncString,
  decryptRsaOaepSha1,
  decryptSymmetricKey,
  deriveMasterKey,
  encryptAes256Hmac,
  encryptBytes,
  encryptSymmetricKey,
  generatePassword,
  generateSymmetricKey,
  masterPasswordHash,
  stretchFromRaw64,
  stretchKey,
  wrapOrgKeyForUser,
  b64encode,
  b64decode,
  type SymmetricKey,
} from "@ciphernest/crypto";

export type VaultCipher = {
  id: string;
  type: number;
  name: string;
  notes: string | null;
  folderId: string | null;
  organizationId: string | null;
  favorite: boolean;
  /** 0 = none, 1 = require master password to reveal/copy */
  reprompt: number;
  deletedDate?: string | null;
  login?: {
    username: string | null;
    password: string | null;
    totp: string | null;
    uris: Array<{ uri: string | null; match: number | null }>;
  };
  card?: {
    cardholderName: string | null;
    brand: string | null;
    number: string | null;
    expMonth: string | null;
    expYear: string | null;
    code: string | null;
  };
  identity?: {
    title: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    username: string | null;
    address1: string | null;
    city: string | null;
    postalCode: string | null;
    country: string | null;
  };
  revisionDate?: string;
  creationDate?: string;
  _enc?: {
    name: string;
    notes: string | null;
    key: string | null;
    login: Record<string, unknown> | null;
  };
};

export type VaultFolder = {
  id: string;
  name: string;
  revisionDate?: string;
  _encName: string;
};

export type VaultSession = {
  email: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  userKey: SymmetricKey;
  deviceIdentifier: string;
  /** Cached decrypted RSA private key (PKCS8) when available */
  privateKeyPkcs8?: Uint8Array | null;
  publicKeyB64?: string | null;
};

/** Portable unlock material for PIN / biometric (never includes master password). */
export type UnlockSnapshot = {
  email: string;
  refreshToken: string;
  userKeyB64: string;
  baseUrl: string;
  deviceIdentifier: string;
};

const STORAGE_KEY = "ciphernest.session.meta";
const REVISION_KEY = "ciphernest.vault.revision";

/** In-memory fallback for extension service workers (no localStorage). */
const memoryStore = new Map<string, string>();

function canUseLocalStorage(): boolean {
  try {
    return typeof globalThis.localStorage !== "undefined";
  } catch {
    return false;
  }
}

function storageGet(key: string): string | null {
  if (canUseLocalStorage()) {
    try {
      return globalThis.localStorage.getItem(key);
    } catch {
      /* private mode */
    }
  }
  return memoryStore.get(key) ?? null;
}

function storageSet(key: string, value: string) {
  memoryStore.set(key, value);
  if (canUseLocalStorage()) {
    try {
      globalThis.localStorage.setItem(key, value);
    } catch {
      /* extension / private mode */
    }
  }
}

function storageRemove(key: string) {
  memoryStore.delete(key);
  if (canUseLocalStorage()) {
    try {
      globalThis.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

/** Seed values before unlock (e.g. stable extension device id). */
export function seedVaultStorage(entries: Record<string, string>) {
  for (const [k, v] of Object.entries(entries)) storageSet(k, v);
}

export class VaultController {
  api: CipherNestApiClient;
  session: VaultSession | null = null;
  ciphers: VaultCipher[] = [];
  trash: VaultCipher[] = [];
  folders: VaultFolder[] = [];
  profile: Record<string, unknown> | null = null;
  /** orgId → decrypted org symmetric key */
  orgKeys: Map<string, SymmetricKey> = new Map();

  constructor(baseUrl: string = DEFAULT_API_BASE) {
    this.api = new CipherNestApiClient(baseUrl);
  }

  setBaseUrl(url: string) {
    this.api = new CipherNestApiClient(url.replace(/\/$/, ""));
  }

  async register(email: string, masterPassword: string, name?: string) {
    const normalized = email.trim().toLowerCase();
    const kdf = { kdf: 0, kdfIterations: 600000 };
    const masterKey = await deriveMasterKey(normalized, masterPassword, kdf);
    const hash = await masterPasswordHash(masterKey, masterPassword);
    const stretchedMaster = await stretchKey(masterKey);
    const userKey = await generateSymmetricKey();
    const protectedUserKey = await encryptSymmetricKey(
      userKey,
      stretchedMaster
    );
    const accountKeys = await createProtectedAccountKeys(userKey);
    await this.api.register({
      email: normalized,
      name: name || normalized,
      masterPasswordHash: hash,
      key: protectedUserKey,
      kdf: kdf.kdf,
      kdfIterations: kdf.kdfIterations,
      keys: {
        publicKey: accountKeys.publicKey,
        encryptedPrivateKey: accountKeys.encryptedPrivateKey,
      },
    });
  }

  async unlock(
    email: string,
    masterPassword: string,
    twoFactorToken?: string
  ): Promise<void> {
    const normalized = email.trim().toLowerCase();
    const pre = await this.api.prelogin(normalized);
    const masterKey = await deriveMasterKey(normalized, masterPassword, {
      kdf: pre.kdf,
      kdfIterations: pre.kdfIterations,
      kdfMemory: pre.kdfMemory,
      kdfParallelism: pre.kdfParallelism,
    });
    const hash = await masterPasswordHash(masterKey, masterPassword);
    const stretchedMaster = await stretchKey(masterKey);

    let deviceIdentifier =
      storageGet("ciphernest.deviceId") || crypto.randomUUID();
    storageSet("ciphernest.deviceId", deviceIdentifier);

    const token = await this.api.loginPassword({
      email: normalized,
      masterPasswordHash: hash,
      deviceIdentifier,
      deviceName: "CipherNest",
      twoFactorToken,
      twoFactorProvider: twoFactorToken ? "0" : undefined,
    });

    const wrapped =
      token.UserDecryptionOptions?.MasterPasswordUnlock
        ?.MasterKeyWrappedUserKey ||
      token.UserDecryptionOptions?.MasterPasswordUnlock
        ?.MasterKeyEncryptedUserKey ||
      token.Key;
    if (!wrapped) throw new Error("Server did not return a user key.");

    const userKey = await decryptSymmetricKey(wrapped, stretchedMaster);

    this.session = {
      email: normalized,
      accessToken: token.access_token,
      refreshToken: token.refresh_token,
      expiresAt: Date.now() + token.expires_in * 1000,
      userKey,
      deviceIdentifier,
    };
    this.persistMeta(token);
    storageSet("ciphernest.email", normalized);
    await this.syncIfNeeded({ force: true });
    await this.ensureAccountKeys();
    await this.loadOrgKeys();
  }

  /** Verify master password online (for re-prompt / 2FA changes). */
  async verifyMasterPassword(masterPassword: string): Promise<boolean> {
    if (!this.session) throw new Error("Locked");
    const pre = await this.api.prelogin(this.session.email);
    const masterKey = await deriveMasterKey(this.session.email, masterPassword, {
      kdf: pre.kdf,
      kdfIterations: pre.kdfIterations,
      kdfMemory: pre.kdfMemory,
      kdfParallelism: pre.kdfParallelism,
    });
    const hash = await masterPasswordHash(masterKey, masterPassword);
    const token = await this.ensureToken();
    try {
      await this.api.verifyPassword(token, hash);
      return true;
    } catch {
      return false;
    }
  }

  async masterPasswordHashForAccount(masterPassword: string): Promise<string> {
    if (!this.session) throw new Error("Locked");
    const pre = await this.api.prelogin(this.session.email);
    const masterKey = await deriveMasterKey(this.session.email, masterPassword, {
      kdf: pre.kdf,
      kdfIterations: pre.kdfIterations,
      kdfMemory: pre.kdfMemory,
      kdfParallelism: pre.kdfParallelism,
    });
    return masterPasswordHash(masterKey, masterPassword);
  }

  /**
   * Change master password: keep the same user key, rewrap under the new
   * master key, update server hash. Device PIN/bio must be re-enabled after.
   */
  async changeMasterPassword(
    currentPassword: string,
    newPassword: string,
    hint?: string | null
  ): Promise<void> {
    if (!this.session) throw new Error("Locked");
    if (newPassword.length < 8) {
      throw new Error("New master password must be at least 8 characters");
    }
    if (currentPassword === newPassword) {
      throw new Error("New password must be different from the current one");
    }

    const email = this.session.email;
    const pre = await this.api.prelogin(email);
    const kdf = {
      kdf: pre.kdf,
      kdfIterations: pre.kdfIterations,
      kdfMemory: pre.kdfMemory,
      kdfParallelism: pre.kdfParallelism,
    };

    const oldMaster = await deriveMasterKey(email, currentPassword, kdf);
    const oldHash = await masterPasswordHash(oldMaster, currentPassword);
    const ok = await this.verifyMasterPassword(currentPassword);
    if (!ok) throw new Error("Current master password is incorrect");

    const newMaster = await deriveMasterKey(email, newPassword, kdf);
    const newHash = await masterPasswordHash(newMaster, newPassword);
    const stretchedNew = await stretchKey(newMaster);
    const wrappedKey = await encryptSymmetricKey(
      this.session.userKey,
      stretchedNew
    );

    const token = await this.ensureToken();
    await this.api.changePassword(token, {
      masterPasswordHash: oldHash,
      newMasterPasswordHash: newHash,
      key: wrappedKey,
      masterPasswordHint: hint ?? null,
    });
  }

  private persistMeta(token: TokenResponse) {
    if (!this.session) return;
    storageSet(
      STORAGE_KEY,
      JSON.stringify({
        email: this.session.email,
        refreshToken: this.session.refreshToken,
        baseUrl: this.api.baseUrl,
        deviceIdentifier: this.session.deviceIdentifier,
      })
    );
    void token;
  }

  lock() {
    this.session = null;
    this.ciphers = [];
    this.trash = [];
    this.folders = [];
    this.profile = null;
    this.orgKeys.clear();
  }

  logout() {
    this.lock();
    storageRemove(STORAGE_KEY);
    storageRemove(REVISION_KEY);
  }

  /** Snapshot for device PIN / biometric unlock (no master password). */
  getUnlockSnapshot(): UnlockSnapshot | null {
    if (!this.session) return null;
    return {
      email: this.session.email,
      refreshToken: this.session.refreshToken,
      userKeyB64: b64encode(this.session.userKey.raw),
      baseUrl: this.api.baseUrl,
      deviceIdentifier: this.session.deviceIdentifier,
    };
  }

  /** Restore session tokens from snapshot without downloading the vault. */
  async hydrateFromSnapshot(snap: UnlockSnapshot): Promise<void> {
    // Always use the packaged API URL (stored baseUrl may be a retired hostname).
    this.setBaseUrl(DEFAULT_API_BASE);
    const raw = b64decode(snap.userKeyB64);
    if (raw.length !== 64) throw new Error("Invalid unlock key");
    const userKey = await stretchFromRaw64(raw);
    storageSet("ciphernest.deviceId", snap.deviceIdentifier);
    storageSet("ciphernest.email", snap.email);

    const refreshed = await this.api.refresh(snap.refreshToken);
    this.session = {
      email: snap.email,
      accessToken: refreshed.access_token,
      refreshToken: refreshed.refresh_token,
      expiresAt: Date.now() + refreshed.expires_in * 1000,
      userKey,
      deviceIdentifier: snap.deviceIdentifier,
    };
    this.persistMeta(refreshed);
  }

  /** Restore a locked vault from PIN/biometric-decrypted snapshot. */
  async unlockFromSnapshot(snap: UnlockSnapshot): Promise<void> {
    await this.hydrateFromSnapshot(snap);
    await this.syncIfNeeded();
    try {
      await this.ensureAccountKeys();
      await this.loadOrgKeys();
    } catch {
      /* autofill does not require org/account keys */
    }
  }

  /** Refresh the access token when the session is hydrated. */
  async keepAlive(): Promise<void> {
    if (!this.session) throw new Error("Locked");
    await this.ensureToken();
  }

  private async fetchRevision(): Promise<number> {
    const token = await this.ensureToken();
    return this.api.revisionDate(token);
  }

  /**
   * Full vault download only when the server revision changed (or force).
   * Returns true when a sync ran.
   */
  async syncIfNeeded(opts?: { force?: boolean }): Promise<boolean> {
    if (!this.session) throw new Error("Locked");
    const force = opts?.force ?? false;
    if (!force) {
      try {
        const remote = await this.fetchRevision();
        const stored = storageGet(REVISION_KEY);
        if (stored && Number(stored) === remote) return false;
      } catch {
        /* fall through to full sync */
      }
    }
    await this.sync();
    return true;
  }

  private async ensureToken(): Promise<string> {
    if (!this.session) throw new Error("Locked");
    if (Date.now() < this.session.expiresAt - 60_000) {
      return this.session.accessToken;
    }
    const refreshed = await this.api.refresh(this.session.refreshToken);
    this.session.accessToken = refreshed.access_token;
    this.session.refreshToken = refreshed.refresh_token;
    this.session.expiresAt = Date.now() + refreshed.expires_in * 1000;
    this.persistMeta(refreshed);
    return this.session.accessToken;
  }

  /** Ensure RSA account keys exist (required for Family org). */
  async ensureAccountKeys(): Promise<void> {
    if (!this.session) return;
    const publicKey =
      (this.profile?.publicKey as string | null) ||
      ((this.profile?.accountKeys as Record<string, unknown> | null)
        ?.publicKeyEncryptionKeyPair as Record<string, unknown> | undefined)
        ?.publicKey;
    const privateEnc =
      (this.profile?.privateKey as string | null) ||
      ((this.profile?.accountKeys as Record<string, unknown> | null)
        ?.publicKeyEncryptionKeyPair as Record<string, unknown> | undefined)
        ?.wrappedPrivateKey;

    if (publicKey && privateEnc) {
      try {
        const pkcs8 = await decryptBytes(String(privateEnc), this.session.userKey);
        this.session.publicKeyB64 = String(publicKey);
        this.session.privateKeyPkcs8 = pkcs8;
      } catch {
        /* regenerate below */
      }
      if (this.session.privateKeyPkcs8) return;
    }

    const keys = await createProtectedAccountKeys(this.session.userKey);
    const token = await this.ensureToken();
    await this.api.updateAccountKeys(
      token,
      keys.publicKey,
      keys.encryptedPrivateKey
    );
    this.session.publicKeyB64 = keys.publicKey;
    this.session.privateKeyPkcs8 = await decryptBytes(
      keys.encryptedPrivateKey,
      this.session.userKey
    );
    await this.syncIfNeeded({ force: true });
  }

  private async loadOrgKeys(): Promise<void> {
    if (!this.session?.privateKeyPkcs8) return;
    this.orgKeys.clear();
    const orgs =
      (this.profile?.organizations as Array<Record<string, unknown>>) || [];
    for (const o of orgs) {
      const encKey = o.key as string | null | undefined;
      if (!encKey || !o.id) continue;
      try {
        const raw = await decryptRsaOaepSha1(
          encKey,
          this.session.privateKeyPkcs8
        );
        const copy = new Uint8Array(raw);
        this.orgKeys.set(
          String(o.id),
          raw.length === 64
            ? await stretchFromRaw64(copy)
            : await stretchKey(copy.buffer)
        );
      } catch {
        /* invite pending or wrong key */
      }
    }
  }

  async sync(): Promise<void> {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    const data = await this.api.sync(token);
    this.profile = data.profile;

    const folders: VaultFolder[] = [];
    for (const f of data.folders || []) {
      const encName = String(f.name ?? "");
      let name = encName;
      try {
        name = (await decryptEncString(encName, this.session.userKey)) || encName;
      } catch {
        name = "[encrypted folder]";
      }
      folders.push({
        id: String(f.id),
        name,
        revisionDate: f.revisionDate as string | undefined,
        _encName: encName,
      });
    }
    this.folders = folders;

    const ciphers: VaultCipher[] = [];
    for (const c of data.ciphers || []) {
      const type = Number(c.type ?? 1);
      const encName = String(c.name ?? "");
      const encNotes = (c.notes as string | null) ?? null;
      const encKey = (c.key as string | null) ?? null;
      const orgId = (c.organizationId as string | null) ?? null;
      let itemKey = this.session.userKey;
      if (orgId && this.orgKeys.has(orgId)) {
        itemKey = this.orgKeys.get(orgId)!;
      }
      if (encKey) {
        try {
          itemKey = await decryptSymmetricKey(encKey, itemKey);
        } catch {
          try {
            itemKey = await decryptSymmetricKey(encKey, this.session.userKey);
          } catch {
            /* leave itemKey */
          }
        }
      }
      let name = encName;
      let notes: string | null = encNotes;
      try {
        name = (await decryptEncString(encName, itemKey)) || encName;
      } catch {
        name = "[unable to decrypt]";
      }
      if (encNotes) {
        try {
          notes = await decryptEncString(encNotes, itemKey);
        } catch {
          notes = null;
        }
      }

      const cipher: VaultCipher = {
        id: String(c.id),
        type,
        name,
        notes,
        folderId: (c.folderId as string | null) ?? null,
        organizationId: orgId,
        favorite: Boolean(c.favorite),
        reprompt: Number(c.reprompt ?? 0),
        deletedDate: (c.deletedDate as string | null) ?? null,
        revisionDate: c.revisionDate as string | undefined,
        creationDate: c.creationDate as string | undefined,
        _enc: {
          name: encName,
          notes: encNotes,
          key: encKey,
          login: (c.login as Record<string, unknown>) || null,
        },
      };

      const dec = async (v: unknown) => {
        if (v == null || v === "") return null;
        try {
          return await decryptEncString(String(v), itemKey);
        } catch {
          return null;
        }
      };

      if (type === 1 && c.login) {
        const login = c.login as Record<string, unknown>;
        const urisRaw = (login.uris as Array<Record<string, unknown>>) || [];
        const uris = [];
        for (const u of urisRaw) {
          uris.push({
            uri: await dec(u.uri),
            match: (u.match as number | null) ?? null,
          });
        }
        cipher.login = {
          username: await dec(login.username),
          password: await dec(login.password),
          totp: await dec(login.totp),
          uris,
        };
      } else if (type === 3 && c.card) {
        const card = c.card as Record<string, unknown>;
        cipher.card = {
          cardholderName: await dec(card.cardholderName),
          brand: await dec(card.brand),
          number: await dec(card.number),
          expMonth: await dec(card.expMonth),
          expYear: await dec(card.expYear),
          code: await dec(card.code),
        };
      } else if (type === 4 && c.identity) {
        const idn = c.identity as Record<string, unknown>;
        cipher.identity = {
          title: await dec(idn.title),
          firstName: await dec(idn.firstName),
          lastName: await dec(idn.lastName),
          email: await dec(idn.email),
          phone: await dec(idn.phone),
          username: await dec(idn.username),
          address1: await dec(idn.address1),
          city: await dec(idn.city),
          postalCode: await dec(idn.postalCode),
          country: await dec(idn.country),
        };
      }
      ciphers.push(cipher);
    }
    this.ciphers = ciphers;
    await this.loadTrash();
    try {
      const rev = await this.fetchRevision();
      storageSet(REVISION_KEY, String(rev));
    } catch {
      /* revision cache is optional */
    }
  }

  private async decryptCipherRow(
    c: Record<string, unknown>,
    itemKeyBase: SymmetricKey
  ): Promise<VaultCipher> {
    const type = Number(c.type ?? 1);
    const encName = String(c.name ?? "");
    const encNotes = (c.notes as string | null) ?? null;
    const encKey = (c.key as string | null) ?? null;
    let itemKey = itemKeyBase;
    if (encKey) {
      try {
        itemKey = await decryptSymmetricKey(encKey, itemKeyBase);
      } catch {
        /* keep */
      }
    }
    const dec = async (v: unknown) => {
      if (v == null || v === "") return null;
      try {
        return await decryptEncString(String(v), itemKey);
      } catch {
        return null;
      }
    };
    let name = encName;
    try {
      name = (await decryptEncString(encName, itemKey)) || encName;
    } catch {
      name = "[unable to decrypt]";
    }
    let notes: string | null = null;
    if (encNotes) {
      try {
        notes = await decryptEncString(encNotes, itemKey);
      } catch {
        notes = null;
      }
    }
    return {
      id: String(c.id),
      type,
      name,
      notes,
      folderId: (c.folderId as string | null) ?? null,
      organizationId: (c.organizationId as string | null) ?? null,
      favorite: Boolean(c.favorite),
      reprompt: Number(c.reprompt ?? 0),
      deletedDate: (c.deletedDate as string | null) ?? null,
    };
  }

  async loadTrash(): Promise<void> {
    if (!this.session) return;
    try {
      const token = await this.ensureToken();
      const res = await this.api.listTrash(token);
      const out: VaultCipher[] = [];
      for (const c of res.data || []) {
        out.push(await this.decryptCipherRow(c, this.session.userKey));
      }
      this.trash = out;
    } catch {
      this.trash = [];
    }
  }

  async saveLogin(input: {
    id?: string;
    name: string;
    username: string;
    password: string;
    notes?: string;
    folderId?: string | null;
    uri?: string | null;
    totp?: string | null;
    favorite?: boolean;
    reprompt?: number;
  }): Promise<void> {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    const cipherKey = await generateSymmetricKey();
    const protectedKey = await encryptSymmetricKey(
      cipherKey,
      this.session.userKey
    );
    const encName = await encryptAes256Hmac(input.name, cipherKey);
    const encUser = await encryptAes256Hmac(input.username, cipherKey);
    const encPass = await encryptAes256Hmac(input.password, cipherKey);
    const encNotes = input.notes
      ? await encryptAes256Hmac(input.notes, cipherKey)
      : null;
    const encTotp = input.totp
      ? await encryptAes256Hmac(input.totp, cipherKey)
      : null;
    const uris = input.uri
      ? [{ uri: await encryptAes256Hmac(input.uri, cipherKey), match: null }]
      : [];

    const body = {
      type: 1,
      name: encName,
      notes: encNotes,
      folderId: input.folderId ?? null,
      organizationId: null,
      key: protectedKey,
      favorite: Boolean(input.favorite),
      reprompt: input.reprompt ?? 0,
      login: {
        username: encUser,
        password: encPass,
        totp: encTotp,
        uris,
      },
    };

    if (input.id) {
      await this.api.updateCipher(token, input.id, body);
    } else {
      await this.api.createCipher(token, body);
    }
    await this.syncIfNeeded({ force: true });
  }

  async saveSecureNote(input: {
    id?: string;
    name: string;
    notes: string;
    folderId?: string | null;
    favorite?: boolean;
  }): Promise<void> {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    const cipherKey = await generateSymmetricKey();
    const protectedKey = await encryptSymmetricKey(
      cipherKey,
      this.session.userKey
    );
    const body = {
      type: 2,
      name: await encryptAes256Hmac(input.name, cipherKey),
      notes: await encryptAes256Hmac(input.notes || "", cipherKey),
      folderId: input.folderId ?? null,
      organizationId: null,
      key: protectedKey,
      favorite: Boolean(input.favorite),
      reprompt: 0,
      secureNote: { type: 0 },
    };
    if (input.id) await this.api.updateCipher(token, input.id, body);
    else await this.api.createCipher(token, body);
    await this.syncIfNeeded({ force: true });
  }

  async saveCard(input: {
    id?: string;
    name: string;
    cardholderName: string;
    brand: string;
    number: string;
    expMonth: string;
    expYear: string;
    code: string;
    notes?: string;
    folderId?: string | null;
    favorite?: boolean;
  }): Promise<void> {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    const cipherKey = await generateSymmetricKey();
    const protectedKey = await encryptSymmetricKey(
      cipherKey,
      this.session.userKey
    );
    const enc = (s: string) => encryptAes256Hmac(s, cipherKey);
    const body = {
      type: 3,
      name: await enc(input.name),
      notes: input.notes ? await enc(input.notes) : null,
      folderId: input.folderId ?? null,
      organizationId: null,
      key: protectedKey,
      favorite: Boolean(input.favorite),
      reprompt: 0,
      card: {
        cardholderName: await enc(input.cardholderName),
        brand: await enc(input.brand),
        number: await enc(input.number),
        expMonth: await enc(input.expMonth),
        expYear: await enc(input.expYear),
        code: await enc(input.code),
      },
    };
    if (input.id) await this.api.updateCipher(token, input.id, body);
    else await this.api.createCipher(token, body);
    await this.syncIfNeeded({ force: true });
  }

  async saveIdentity(input: {
    id?: string;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address1?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    notes?: string;
    folderId?: string | null;
    favorite?: boolean;
  }): Promise<void> {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    const cipherKey = await generateSymmetricKey();
    const protectedKey = await encryptSymmetricKey(
      cipherKey,
      this.session.userKey
    );
    const enc = (s: string) => encryptAes256Hmac(s || "", cipherKey);
    const body = {
      type: 4,
      name: await enc(input.name),
      notes: input.notes ? await enc(input.notes) : null,
      folderId: input.folderId ?? null,
      organizationId: null,
      key: protectedKey,
      favorite: Boolean(input.favorite),
      reprompt: 0,
      identity: {
        title: null,
        firstName: await enc(input.firstName),
        middleName: null,
        lastName: await enc(input.lastName),
        address1: await enc(input.address1 || ""),
        address2: null,
        address3: null,
        city: await enc(input.city || ""),
        state: null,
        postalCode: await enc(input.postalCode || ""),
        country: await enc(input.country || ""),
        company: null,
        email: await enc(input.email),
        phone: await enc(input.phone),
        ssn: null,
        username: null,
        passportNumber: null,
        licenseNumber: null,
      },
    };
    if (input.id) await this.api.updateCipher(token, input.id, body);
    else await this.api.createCipher(token, body);
    await this.syncIfNeeded({ force: true });
  }

  async toggleFavorite(id: string): Promise<void> {
    const existing = this.ciphers.find((c) => c.id === id);
    if (!existing) throw new Error("Not found");
    if (existing.type === 1 && existing.login) {
      await this.saveLogin({
        id: existing.id,
        name: existing.name,
        username: existing.login.username || "",
        password: existing.login.password || "",
        notes: existing.notes || undefined,
        folderId: existing.folderId,
        uri: existing.login.uris[0]?.uri || null,
        totp: existing.login.totp,
        favorite: !existing.favorite,
      });
      return;
    }
    if (existing.type === 2) {
      await this.saveSecureNote({
        id: existing.id,
        name: existing.name,
        notes: existing.notes || "",
        folderId: existing.folderId,
        favorite: !existing.favorite,
      });
      return;
    }
    if (existing.type === 3 && existing.card) {
      await this.saveCard({
        id: existing.id,
        name: existing.name,
        cardholderName: existing.card.cardholderName || "",
        brand: existing.card.brand || "",
        number: existing.card.number || "",
        expMonth: existing.card.expMonth || "",
        expYear: existing.card.expYear || "",
        code: existing.card.code || "",
        notes: existing.notes || undefined,
        folderId: existing.folderId,
        favorite: !existing.favorite,
      });
      return;
    }
    if (existing.type === 4 && existing.identity) {
      await this.saveIdentity({
        id: existing.id,
        name: existing.name,
        firstName: existing.identity.firstName || "",
        lastName: existing.identity.lastName || "",
        email: existing.identity.email || "",
        phone: existing.identity.phone || "",
        address1: existing.identity.address1 || undefined,
        city: existing.identity.city || undefined,
        postalCode: existing.identity.postalCode || undefined,
        country: existing.identity.country || undefined,
        notes: existing.notes || undefined,
        folderId: existing.folderId,
        favorite: !existing.favorite,
      });
    }
  }

  async createFolder(name: string): Promise<void> {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    const enc = await encryptAes256Hmac(name, this.session.userKey);
    await this.api.createFolder(token, enc);
    await this.syncIfNeeded({ force: true });
  }

  async deleteCipher(id: string): Promise<void> {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    await this.api.deleteCipher(token, id, true);
    await this.syncIfNeeded({ force: true });
  }

  async restoreCipher(id: string): Promise<void> {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    await this.api.restoreCipher(token, id);
    await this.syncIfNeeded({ force: true });
  }

  async moveToFolder(id: string, folderId: string | null): Promise<void> {
    const existing = this.ciphers.find((c) => c.id === id);
    if (!existing || !this.session) throw new Error("Not found");
    if (existing.type !== 1 || !existing.login) {
      throw new Error("Only logins can be moved in v1 helper");
    }
    await this.saveLogin({
      id: existing.id,
      name: existing.name.startsWith("[") ? "Item" : existing.name,
      username: existing.login.username || "",
      password: existing.login.password || "",
      notes: existing.notes || undefined,
      folderId,
      uri: existing.login.uris[0]?.uri || null,
    });
  }

  async createOrganization(name: string, collectionName = "Shared"): Promise<void> {
    if (!this.session) throw new Error("Locked");
    await this.ensureAccountKeys();
    if (!this.session.publicKeyB64) {
      throw new Error("Account public key missing");
    }
    const keys = await createOrganizationKeys(this.session.publicKeyB64);
    const token = await this.ensureToken();
    await this.api.createOrganization(token, {
      name,
      billingEmail: this.session.email,
      key: keys.key,
      keys: {
        publicKey: keys.publicKey,
        encryptedPrivateKey: keys.encryptedPrivateKey,
      },
      collectionName,
    });
    await this.syncIfNeeded({ force: true });
    await this.loadOrgKeys();
  }

  async inviteToOrg(orgId: string, email: string): Promise<void> {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    await this.api.inviteOrgUsers(token, orgId, [email.trim().toLowerCase()], true);
    await this.syncIfNeeded({ force: true });
  }

  async confirmOrgMember(orgId: string, memberId: string): Promise<void> {
    if (!this.session) throw new Error("Locked");
    const orgKey = this.orgKeys.get(orgId);
    if (!orgKey) throw new Error("Organization key not available — unlock as owner.");
    const token = await this.ensureToken();
    const pubs = await this.api.orgPublicKeys(token, orgId, [memberId]);
    const entry =
      pubs.data.find((d) => d.id === memberId) ||
      pubs.data.find((d) => d.userId === memberId);
    if (!entry?.key) {
      throw new Error(
        "Invitee has no public key yet. They must unlock CipherNest once first."
      );
    }
    const wrapped = await wrapOrgKeyForUser(orgKey.raw, entry.key);
    await this.api.confirmOrgUser(token, orgId, entry.id, wrapped);
    await this.syncIfNeeded({ force: true });
  }

  async listOrgMembers(orgId: string) {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    return this.api.listOrgUsers(token, orgId);
  }

  async createCollection(orgId: string, name: string): Promise<void> {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    await this.api.createCollection(token, orgId, name);
    await this.syncIfNeeded({ force: true });
  }

  /**
   * Import plaintext items (from CSV / unencrypted vault JSON).
   * Encrypts client-side then batch-posts to /api/ciphers/import.
   */
  async importPlainItems(
    items: Array<{
      type?: number;
      name: string;
      notes?: string;
      folder?: string;
      favorite?: boolean;
      login?: {
        username?: string;
        password?: string;
        totp?: string;
        uris?: string[];
      };
      card?: Record<string, string>;
      identity?: Record<string, string>;
    }>
  ): Promise<number> {
    if (!this.session) throw new Error("Locked");
    if (!items.length) return 0;

    const folderNames: string[] = [];
    const folderIndex = new Map<string, number>();
    const relationships: Array<[number, number]> = [];
    const ciphers: Record<string, unknown>[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      const folder = (item.folder || "").trim();
      if (folder && !folderIndex.has(folder)) {
        folderIndex.set(folder, folderNames.length);
        folderNames.push(folder);
      }
      if (folder && folderIndex.has(folder)) {
        relationships.push([i, folderIndex.get(folder)!]);
      }

      const cipherKey = await generateSymmetricKey();
      const protectedKey = await encryptSymmetricKey(
        cipherKey,
        this.session.userKey
      );
      const type = item.type ?? (item.login ? 1 : item.card ? 3 : item.identity ? 4 : 2);
      const enc = (s: string) => encryptAes256Hmac(s || "", cipherKey);
      const base: Record<string, unknown> = {
        type,
        name: await enc(item.name || "Imported"),
        notes: item.notes ? await enc(item.notes) : null,
        folderId: null,
        organizationId: null,
        key: protectedKey,
        favorite: Boolean(item.favorite),
        reprompt: 0,
      };

      if (type === 1) {
        const uris = (item.login?.uris || []).filter(Boolean);
        base.login = {
          username: await enc(item.login?.username || ""),
          password: await enc(item.login?.password || ""),
          totp: item.login?.totp ? await enc(item.login.totp) : null,
          uris: await Promise.all(
            uris.map(async (u) => ({ uri: await enc(u), match: null }))
          ),
        };
      } else if (type === 2) {
        base.secureNote = { type: 0 };
      } else if (type === 3) {
        const card = item.card || {};
        base.card = {
          cardholderName: await enc(card.cardholderName || ""),
          brand: await enc(card.brand || ""),
          number: await enc(card.number || ""),
          expMonth: await enc(card.expMonth || ""),
          expYear: await enc(card.expYear || ""),
          code: await enc(card.code || ""),
        };
      } else if (type === 4) {
        const idn = item.identity || {};
        base.identity = {
          firstName: await enc(idn.firstName || ""),
          lastName: await enc(idn.lastName || ""),
          email: await enc(idn.email || ""),
          phone: await enc(idn.phone || ""),
          address1: await enc(idn.address1 || ""),
          city: await enc(idn.city || ""),
        };
      }
      ciphers.push(base);
    }

    const folders = await Promise.all(
      folderNames.map(async (name) => ({
        name: await encryptAes256Hmac(name, this.session!.userKey),
      }))
    );

    const token = await this.ensureToken();
    const res = await this.api.importCiphers(token, {
      ciphers,
      folders,
      folderRelationships: relationships,
    });
    await this.syncIfNeeded({ force: true });
    return res.imported ?? ciphers.length;
  }

  /** Unencrypted CipherNest JSON for backup / migrate-out. */
  exportVaultJson(): string {
    const folders = this.folders.map((f, i) => ({
      id: f.id,
      name: f.name,
      _index: i,
    }));
    const folderMap = new Map(folders.map((f) => [f.id, f._index]));
    const items = this.ciphers.map((c) => {
      const base: Record<string, unknown> = {
        type: c.type,
        name: c.name,
        notes: c.notes,
        favorite: c.favorite,
        folderId: c.folderId,
        reprompt: c.reprompt,
      };
      if (c.type === 1 && c.login) {
        base.login = {
          username: c.login.username,
          password: c.login.password,
          totp: c.login.totp,
          uris: c.login.uris.map((u) => ({ uri: u.uri, match: u.match })),
        };
      } else if (c.type === 2) {
        base.secureNote = { type: 0 };
      } else if (c.type === 3 && c.card) {
        base.card = c.card;
      } else if (c.type === 4 && c.identity) {
        base.identity = c.identity;
      }
      return base;
    });
    return JSON.stringify(
      {
        encrypted: false,
        folders: folders.map(({ id, name }) => ({ id, name })),
        items,
        folderIndexes: Object.fromEntries(
          this.ciphers.map((c, i) => [
            i,
            c.folderId ? folderMap.get(c.folderId) ?? null : null,
          ])
        ),
      },
      null,
      2
    );
  }

  exportVaultCsv(): string {
    const header = "name,folder,username,password,uri,totp,notes,type";
    const rows = this.ciphers.map((c) => {
      const folder =
        this.folders.find((f) => f.id === c.folderId)?.name || "";
      const esc = (s: string) => `"${String(s || "").replace(/"/g, '""')}"`;
      if (c.type === 1 && c.login) {
        return [
          esc(c.name),
          esc(folder),
          esc(c.login.username || ""),
          esc(c.login.password || ""),
          esc(c.login.uris[0]?.uri || ""),
          esc(c.login.totp || ""),
          esc(c.notes || ""),
          "login",
        ].join(",");
      }
      return [
        esc(c.name),
        esc(folder),
        "",
        "",
        "",
        "",
        esc(c.notes || ""),
        c.type === 2 ? "note" : c.type === 3 ? "card" : "identity",
      ].join(",");
    });
    return [header, ...rows].join("\n");
  }

  async createTextSend(input: {
    name: string;
    text: string;
    password?: string;
    daysValid?: number;
    maxAccessCount?: number | null;
  }): Promise<{ id: string; accessUrl: string }> {
    if (!this.session) throw new Error("Locked");
    const sendKey = await generateSymmetricKey();
    const encText = await encryptAes256Hmac(input.text, sendKey);
    const encName = await encryptAes256Hmac(input.name, sendKey);
    const days = input.daysValid ?? 7;
    const deletionDate = new Date(Date.now() + days * 86400000).toISOString();
    const token = await this.ensureToken();
    const res = await this.api.createSend(token, {
      type: 0,
      name: encName,
      key: b64encode(sendKey.raw),
      password: input.password || null,
      text: { text: encText },
      deletionDate,
      maxAccessCount: input.maxAccessCount ?? null,
      hideEmail: true,
    });
    const id = String(res.id);
    return {
      id,
      accessUrl: `${this.api.baseUrl}/api/sends/access/${id}#${b64encode(sendKey.raw)}`,
    };
  }

  async listSends() {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    return this.api.listSends(token);
  }

  async deleteSend(id: string) {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    await this.api.deleteSend(token, id);
  }

  async listAttachments(cipherId: string) {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    return this.api.listAttachments(token, cipherId);
  }

  async uploadAttachment(cipherId: string, file: File): Promise<void> {
    if (!this.session) throw new Error("Locked");
    if (file.size > 700_000) throw new Error("File too large (max ~700KB)");
    const buf = new Uint8Array(await file.arrayBuffer());
    const enc = await encryptBytes(buf, this.session.userKey);
    const token = await this.ensureToken();
    await this.api.createAttachment(token, cipherId, {
      fileName: file.name,
      size: file.size,
      key: "user",
      data: enc,
    });
  }

  async downloadAttachment(
    cipherId: string,
    attId: string
  ): Promise<{ fileName: string; bytes: Uint8Array }> {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    const att = await this.api.getAttachment(token, cipherId, attId);
    const data = String(att.data || "");
    const bytes = await decryptBytes(data, this.session.userKey);
    return { fileName: String(att.fileName || "file"), bytes };
  }

  async deleteAttachment(cipherId: string, attId: string) {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    await this.api.deleteAttachment(token, cipherId, attId);
  }

  async setupAccountTwoFactor() {
    if (!this.session) throw new Error("Locked");
    const token = await this.ensureToken();
    return this.api.setupTwoFactor(token);
  }

  async enableAccountTwoFactor(
    secret: string,
    code: string,
    masterPassword: string
  ) {
    if (!this.session) throw new Error("Locked");
    const hash = await this.masterPasswordHashForAccount(masterPassword);
    const token = await this.ensureToken();
    await this.api.enableTwoFactor(token, {
      secret,
      code,
      masterPasswordHash: hash,
    });
    await this.syncIfNeeded({ force: true });
  }

  async disableAccountTwoFactor(masterPassword: string) {
    if (!this.session) throw new Error("Locked");
    const hash = await this.masterPasswordHashForAccount(masterPassword);
    const token = await this.ensureToken();
    await this.api.disableTwoFactor(token, hash);
    await this.syncIfNeeded({ force: true });
  }

  generatePassword = generatePassword;
}

export { DEFAULT_API_BASE, generatePassword };
export {
  hostFromUri,
  hostsMatch,
  looksLikeOtp,
  scoreHostMatch,
  stripWww,
} from "./hostMatch";
